// Shared layered-image rendering logic for a configured SOMMET watch.
//
// A configured watch is always rendered as 4 stacked photo layers
// (strap -> case -> dial -> hands). Wherever a real photo exists for the
// selected color/finish, that photo is used as-is; for finishes without
// their own photo, the closest real photo is reused with a CSS filter to
// reach the target color. This file is the single source of truth for
// that logic so the Configurator's "result" preview, the cart drawer and
// the checkout page all show the exact combination the customer actually
// picked — never a generic placeholder image.

// Uniform master layers (each 1400×1400, part placed at its correct
// anatomical position). Just stack them with `absolute inset-0 object-contain`.
export const MASTER = {
  case: "/watches/master/case-silver.png",
  dial: {
    black: "/watches/master/dial-black.png",
    green: "/watches/master/dial-green.png",
    dark_blue: "/watches/master/dial-blue.png",
    silver: "/watches/master/dial-silver.png",
  },
  hands: {
    silver: "/watches/master/hands-silver.png",
    bronze: "/watches/master/hands-bronze.png",
    gold: "/watches/master/hands-gold.png",
  },
  strap: {
    leather: "/watches/master/strap-brown.png",
    steel: "/watches/master/strap-silver.png",
  },
};

// Which real base photo each dial/hands/strap color is built from —
// direct photos where we have them, closest real photo otherwise.
const DIAL_BASE_FOR = { black: "black", silver: "silver", white: "silver", green: "green", dark_blue: "dark_blue" };
const HANDS_BASE_FOR = { silver: "silver", bronze: "bronze", gold: "gold" };
const STRAP_BASE_FOR = { steel: "steel", leather: "leather" };

export const DIAL_FILTER = {
  black: "none",
  silver: "none",
  white: "brightness(1.28) contrast(0.96) saturate(0.9)",
  green: "none",
  dark_blue: "none",
};

export const CASE_FILTER = {
  silver: "none",
  black: "saturate(0) brightness(0.32) contrast(1.6)",
  gold: "sepia(1) saturate(3.6) hue-rotate(-12deg) brightness(1.15) contrast(1.05)",
  rose_gold: "sepia(0.9) saturate(2.4) hue-rotate(-25deg) brightness(1.08) contrast(1.02)",
};

export const HANDS_FILTER = {
  silver: "none",
  bronze: "none",
  gold: "none",
};

export const STRAP_FILTER = {
  leather: "none",
  steel: "none",
};

/**
 * Given a config { case, dial, hands, strap } (the color/finish ids used
 * throughout the app), returns everything needed to render the four
 * stacked layers of the actual configured watch.
 *
 * `customOptions` (optional) is the flat list from /api/custom-options —
 * admin-added colors/images. When the selected id for a category matches
 * a custom option that has its own photo, that photo is used directly
 * (no filter) instead of the built-in base+filter combo.
 */
export const getWatchLayers = (config = {}, customOptions = []) => {
  const customFor = (category, id) =>
    customOptions.find((o) => o.category === category && o.id === id && o.image_data);

  const dialCustom = customFor("dial", config.dial);
  const handsCustom = customFor("hands", config.hands);
  const strapCustom = customFor("strap", config.strap);
  const caseCustom = customFor("case", config.case);

  const dialBase = MASTER.dial[DIAL_BASE_FOR[config.dial]] || MASTER.dial.black;
  const handsBase = MASTER.hands[HANDS_BASE_FOR[config.hands]] || MASTER.hands.silver;
  const strapBase = MASTER.strap[STRAP_BASE_FOR[config.strap]] || MASTER.strap.leather;

  return {
    strap: strapCustom ? { src: strapCustom.image_data, filter: "none" } : { src: strapBase, filter: STRAP_FILTER[config.strap] || "none" },
    case: caseCustom ? { src: caseCustom.image_data, filter: "none" } : { src: MASTER.case, filter: CASE_FILTER[config.case] || "none" },
    dial: dialCustom ? { src: dialCustom.image_data, filter: "none" } : { src: dialBase, filter: DIAL_FILTER[config.dial] || "none" },
    hands: handsCustom ? { src: handsCustom.image_data, filter: "none" } : { src: handsBase, filter: HANDS_FILTER[config.hands] || "none" },
  };
};

